// src/page/hospitality/ListFacility.jsx
import React, { useState, useEffect, useContext } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import axios from "axios";
import "../../styles/ListFacility.css";
import { useAuth } from "../../context/AuthContext";

// ---------------- Google Geocoding ----------------
const GOOGLE_API_KEY = "YOUR_GOOGLE_API_KEY_HERE";
const geocodeAddress = async (address) => {
  if (!address) return { lat: "", lng: "" };
  try {
    const res = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
      params: { address, key: GOOGLE_API_KEY },
    });
    const loc = res.data.results[0]?.geometry?.location;
    return loc ? { lat: loc.lat, lng: loc.lng } : { lat: "", lng: "" };
  } catch (err) {
    console.error("Geocoding error:", err);
    return { lat: "", lng: "" };
  }
};

// ---------------- AXIOS INSTANCE ----------------
const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function ListFacility() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [mediaFiles, setMediaFiles] = useState({ images: [], video: null });
  const [form, setForm] = useState(null);
  const [branchMenus, setBranchMenus] = useState({});

  const { lang } = useContext(LanguageContext);
  const t = getTranslation(lang);

  const [imageNames, setImageNames] = useState([]);
  const [videoName, setVideoName] = useState("");
  const [menuNames, setMenuNames] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const PLAN_FEES = {
    EUR: {
      basic: 5,
      pro: 10,
      premium: 15,
    },
    ETB: {
      basic: 100,
      pro: 200,
      premium: 300,
    },
  };

  const IBAN = "BE67 9673 4428 4587";
  const EUR_ACCOUNT_NAME = "Asnakew Bewketu Belete";

  const TELEBIRR = "xxx";
  const ETB_ACCOUNT_NAME = "xxx";


  const PLAN_LIMITS = {
    basic: { images: 4, video: false },
    pro: { images: 7, video: false },
    premium: { images: 10, video: true },
  };


  useEffect(() => {
    if (user) {
      setForm({
        id: "",
        ownerEmail: user.email,
        name: "",
        category: "Cafes",
        rank: "", // ⭐ 
        whyChoose: "",
        verified: true,
        paid_plan: false,
        membership_plan: "basic",
        images: [],
        menus: {},
        branches: [
          {
            branch_name: "",
            address: "",
            phone: "",
            whatsapp: "",
            telegram: "",
            services: "",
            monday_open: "",
            monday_close: "",
            tuesday_open: "",
            tuesday_close: "",
            wednesday_open: "",
            wednesday_close: "",
            thursday_open: "",
            thursday_close: "",
            friday_open: "",
            friday_close: "",
            saturday_open: "",
            saturday_close: "",
            sunday_open: "",
            sunday_close: "",
            lat: "",
            lng: "",
          },
        ],
      });
    }
  }, [user]);

  useEffect(() => {
    if (form?.membership_plan !== "premium" && mediaFiles.video) {
      setMediaFiles((prev) => ({ ...prev, video: null }));
      setVideoName("");
    }
  }, [form?.membership_plan, mediaFiles.video]);

  if (!user || !form) return null;


  const ETHIOPIA_KEYWORDS = [
    "addis ababa",
    "addis",
    "ethiopia",
    "bole",
    "megenagna",
    "piassa",
    "sar bet",
    "cmc",
    "gerji",
    "kirkos",
    "lideta",
    "yeka",
    "akaki",
    "kolfe",
    "gulele",
    "nifas silk",
    "bole medhanialem",
  ];

  const isEthiopiaAddress = (text = "") => {
    const normalized = text.trim().toLowerCase();
    return ETHIOPIA_KEYWORDS.some((keyword) => normalized.includes(keyword));
  };

  const shouldUseETB = form.branches.some((branch) =>
    isEthiopiaAddress(branch.address)
  );

  const currentCurrency = shouldUseETB ? "ETB" : "EUR";

  const getPlanFee = (planKey) => PLAN_FEES[currentCurrency][planKey];

  const getFormattedFee = (planKey) => {
    const amount = getPlanFee(planKey);
    return currentCurrency === "ETB" ? `${amount} ETB` : `€${amount}`;
  };


  const paymentLabel = currentCurrency === "ETB"
    ? "Telebirr"
    : t.list_facility.iban_label;

  const paymentValue = currentCurrency === "ETB"
    ? TELEBIRR
    : IBAN;

  const accountNameValue = currentCurrency === "ETB"
    ? ETB_ACCOUNT_NAME
    : EUR_ACCOUNT_NAME;


  // ---------------- Handlers ----------------
  const handleInput = (e, branchIndex = null, field = null) => {
    const { name, value } = e.target;
    if (branchIndex !== null && field) {
      setForm((prev) => {
        const updatedBranches = prev.branches.map((b, i) =>
          i === branchIndex ? { ...b, [field]: value } : b
        );
        return { ...prev, branches: updatedBranches };
      });
      if (field === "address") {
        geocodeAddress(value).then((coords) => {
          setForm((prev) => {
            const updatedBranches = prev.branches.map((b, i) =>
              i === branchIndex ? { ...b, lat: coords.lat, lng: coords.lng } : b
            );
            return { ...prev, branches: updatedBranches };
          });
        });
      }
    } else setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addBranch = () => {
    setForm((prev) => ({
      ...prev,
      branches: [
        ...prev.branches,
        {
          branch_name: "",
          address: "",
          phone: "",
          whatsapp: "",
          telegram: "",
          services: "",
          monday_open: "",
          monday_close: "",
          tuesday_open: "",
          tuesday_close: "",
          wednesday_open: "",
          wednesday_close: "",
          thursday_open: "",
          thursday_close: "",
          friday_open: "",
          friday_close: "",
          saturday_open: "",
          saturday_close: "",
          sunday_open: "",
          sunday_close: "",
          lat: "",
          lng: "",
        },
      ],
    }));
  };


  const handleFileChange = (e, type, branchIndex = null) => {
    if (type === "images") {
      const imageLimit = PLAN_LIMITS[form.membership_plan]?.images || 4;
      const files = Array.from(e.target.files).slice(0, imageLimit);

      setImageNames(files.map((f) => f.name));
      setMediaFiles((prev) => ({
        ...prev,
        images: files,
      }));
    } else if (type === "video") {
      if (form.membership_plan !== "premium") return;

      const file = e.target.files[0];
      setVideoName(file ? file.name : "");
      setMediaFiles((prev) => ({ ...prev, video: file || null }));
    } else if (type === "menu" && branchIndex !== null) {
      const file = e.target.files[0];
      setMenuNames((prev) => ({ ...prev, [branchIndex]: file?.name || "" }));
      setBranchMenus((prev) => ({ ...prev, [branchIndex]: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setStatus("");

    // ⭐ Convert number to stars
    let updatedForm = { ...form };

    if (form.category === "Hotels" && form.rank) {
      updatedForm.rank = parseInt(form.rank);
    } else {
      updatedForm.rank = null;
    }

    const data = new FormData();
    data.append("json", JSON.stringify(updatedForm));
    mediaFiles.images.forEach((file) => data.append("images", file));
    if (form.membership_plan === "premium" && mediaFiles.video) {
      data.append("video", mediaFiles.video);
    }

    Object.entries(branchMenus).forEach(([branchIndex, file]) => {
      if (file) data.append(`menu_${branchIndex}`, file);
    });

    try {
      await api.post("/api/facilities", data);
      setStatus(t.list_facility.success);

      setForm((prev) => ({
        ...prev,
        name: "",
        whyChoose: "",
        membership_plan: "basic",
        branches: prev.branches.map(() => ({
          branch_name: "",
          address: "",
          phone: "",
          whatsapp: "",
          telegram: "",
          services: "",
          monday_open: "",
          monday_close: "",
          tuesday_open: "",
          tuesday_close: "",
          wednesday_open: "",
          wednesday_close: "",
          thursday_open: "",
          thursday_close: "",
          friday_open: "",
          friday_close: "",
          saturday_open: "",
          saturday_close: "",
          sunday_open: "",
          sunday_close: "",
          lat: "",
          lng: "",
        })),
      }));

      setMediaFiles({ images: [], video: null });
      setBranchMenus({});
      setImageNames([]);
      setVideoName("");
      setMenuNames({});

      setTimeout(() => {
        setOpen(false);
        setStatus("");
      }, 800);
    } catch (err) {
      console.error(err.response?.data || err);
      setStatus(err.response?.data?.message || t.list_facility.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {!open && (
        <div style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", zIndex: 1000 }}>
          <button
            onClick={() => setOpen(true)}
            style={{
              backgroundColor: "#1E40AF",
              color: "#fff",
              padding: "12px 24px",
              borderRadius: "12px",
              fontWeight: "bold",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
          >
            {t.list_facility.button_open}
          </button>
        </div>
      )}

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            zIndex: 999,
            padding: "20px",
            overflowY: "auto",
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{
              backgroundColor: "#0f172a",
              color: "#e0e0e0",
              borderRadius: "15px",
              padding: "20px",
              maxWidth: "600px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            }}
          >
            <h2 style={{ textAlign: "center", color: "#38bdf8" }}>{t.list_facility.title}</h2>

            {/* Facility fields */}
            <input name="ownerEmail" value={form.ownerEmail} onChange={handleInput} placeholder={t.list_facility.owner_email} required />
            <input name="name" value={form.name} onChange={handleInput} placeholder={t.list_facility.facility_name} required />
            <textarea name="whyChoose" value={form.whyChoose} onChange={handleInput} placeholder={t.list_facility.why_choose} rows={3} />
            <select name="category" value={form.category} onChange={handleInput} required>
              <option value="Cafes">{t.list_facility.cafes}</option>
              <option value="Restaurants">Restaurants</option>
              <option value="Hotels">Hotels</option>
              <option value="Bars">Bars</option>
              <option value="Clubs">Clubs</option>
            </select>

            {/* ⭐ Show only if Hotels */}
            {form.category === "Hotels" && (
              <input
                type="number"
                min="1"
                max="5"
                name="rank"
                value={form.rank}
                onChange={handleInput}
                placeholder={t.list_facility.stars_placeholder}
              />
            )}

            <select name="membership_plan" value={form.membership_plan} onChange={handleInput} style={{ padding: "8px" }}>
              <option value="basic">{t.list_facility.basic}</option>
              <option value="pro">{t.list_facility.pro}</option>
              <option value="premium">{t.list_facility.premium}</option>
            </select>

            <label style={{ color: "#FF6F00", fontWeight: "bold" }}>
              {t.list_facility.images_label_dynamic} ({t.list_facility.max_label} {PLAN_LIMITS[form.membership_plan].images}): ({imageNames.length}/{PLAN_LIMITS[form.membership_plan].images})
            </label>

            <label style={{ cursor: "pointer", color: "#facc15", background: "#334155", padding: "6px 12px", borderRadius: "6px", width: "fit-content" }}>
              {t.list_facility.choose_file}
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleFileChange(e, "images")}
                style={{ display: "none" }}
              />
            </label>
            <div
              style={{
                fontSize: "14px",
                background: imageNames.length > 0 ? "#1e40af" : "transparent",
                color: imageNames.length > 0 ? "#fff" : "#ccc",
                padding: "4px 8px",
                borderRadius: "6px",
                minHeight: "20px",
                wordBreak: "break-word",
              }}
            >
              {imageNames.length > 0
                ? imageNames.join(", ")
                : t.list_facility.no_file_chosen}
            </div>

            {form.membership_plan === "premium" && (
              <>
                <label style={{ color: "#FF6F00", fontWeight: "bold" }}>
                  {t.list_facility.video_label}
                </label>

                <label
                  style={{
                    cursor: "pointer",
                    color: "#facc15",
                    background: "#334155",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    width: "fit-content",
                  }}
                >
                  {t.list_facility.choose_file}
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileChange(e, "video")}
                    style={{ display: "none" }}
                  />
                </label>

                <div
                  style={{
                    fontSize: "14px",
                    background: videoName ? "#1e40af" : "transparent",
                    color: videoName ? "#fff" : "#ccc",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    minHeight: "20px",
                    wordBreak: "break-word",
                  }}
                >
                  {videoName || t.list_facility.no_file_chosen}
                </div>
              </>
            )}

            {/* Branches */}
            <h3 style={{ color: "white" }}>
              {t.list_facility.branch_details}
            </h3>
            {form.branches.map((branch, i) => (
              <div key={i} style={{ border: "1px solid #334155", borderRadius: "10px", padding: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
                <input value={branch.branch_name} onChange={(e) => handleInput(e, i, "branch_name")} placeholder={t.list_facility.branch_name} />
                <input value={branch.address} onChange={(e) => handleInput(e, i, "address")} placeholder={t.list_facility.address} />
                <input value={branch.phone} onChange={(e) => handleInput(e, i, "phone")} placeholder={t.list_facility.phone} />
                <input value={branch.whatsapp} onChange={(e) => handleInput(e, i, "whatsapp")} placeholder={t.list_facility.whatsapp} />
                <input value={branch.telegram} onChange={(e) => handleInput(e, i, "telegram")} placeholder={t.list_facility.telegram} />
                <input value={branch.services} onChange={(e) => handleInput(e, i, "services")} placeholder={t.list_facility.services} />

                {/* Operating Hours */}
                {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                  <div key={day} style={{ display: "flex", gap: "8px" }}>
                    <input value={branch[`${day}_open`]} onChange={(e) => handleInput(e, i, `${day}_open`)} placeholder={`${t.list_facility[day]} ${t.list_facility.open}`} />
                    <input value={branch[`${day}_close`]} onChange={(e) => handleInput(e, i, `${day}_close`)} placeholder={`${t.list_facility[day]} ${t.list_facility.close}`} />
                  </div>
                ))}

                {/* Menu upload per branch */}
                <div style={{ display: "flex", color: "#facc15", flexDirection: "column", gap: "6px" }}>
                  <label style={{ color: "#FF6F00", fontWeight: "bold" }}>
                    {t.list_facility.menu}:
                  </label>

                  <label
                    style={{
                      cursor: "pointer",
                      background: "#334155",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      width: "fit-content"
                    }}
                  >
                    {t.list_facility.choose_file}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, "menu", i)}
                      style={{ display: "none" }}
                    />
                  </label>

                  <div
                    style={{
                      fontSize: "13px",
                      background: menuNames[i] ? "#1e40af" : "transparent",
                      color: menuNames[i] ? "#fff" : "#ccc",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      minHeight: "20px",
                      wordBreak: "break-word",
                    }}
                  >
                    {menuNames[i] || t.list_facility.no_file_chosen}
                  </div>
                </div>
              </div>
            ))}

            <button type="button" onClick={addBranch} style={{ backgroundColor: "#2563EB", color: "#fff", padding: "8px 16px", borderRadius: "8px", alignSelf: "flex-start" }}>{t.list_facility.add_branch}</button>

            <div
              style={{
                backgroundColor: "#1e293b",
                border: "1px solid #334155",
                borderRadius: "10px",
                padding: "12px",
                color: "#ffffff",
                lineHeight: 1.6,
              }}
            >
              <p style={{ margin: 0, fontWeight: "bold", color: "#facc15" }}>
                {t.list_facility.payment_information}
              </p>

              <p style={{ margin: "8px 0 0 0", color: "#ffffff" }}>
                {t.list_facility.payment_instruction}
              </p>

              <p style={{ margin: "8px 0 0 0", color: "#ffffff" }}>
                <strong>{t.list_facility.membership_plan_label}:</strong> {form.membership_plan}
              </p>

              <p style={{ margin: "4px 0 0 0", color: "#ffffff" }}>
                <strong>{t.list_facility.fee_label}:</strong> {getFormattedFee(form.membership_plan)} {t.list_facility.per_month}
              </p>

              <p style={{ margin: "4px 0 0 0", color: "#ffffff" }}>
                <strong>{paymentLabel}:</strong> {paymentValue}
              </p>

              <p style={{ margin: "4px 0 0 0", color: "#ffffff" }}>
                <strong>{t.list_facility.account_name_label}:</strong> {accountNameValue}
              </p>
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "10px" }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: isSubmitting ? "#64748b" : "#1E90FF",
                  color: "#fff",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  opacity: isSubmitting ? 0.8 : 1,
                }}
              >
                {isSubmitting ? t.list_facility.submitting : t.list_facility.submit}
              </button>
              <button type="button" onClick={() => setOpen(false)} style={{ backgroundColor: "#6b7280", color: "#fff", padding: "10px 20px", borderRadius: "8px" }}>{t.list_facility.cancel}</button>
            </div>

            {status && <p style={{ textAlign: "center" }}>{status}</p>}
          </form>
        </div>
      )}
    </>
  );
}
